import {
  Car,
  ChevronRight,
  MapPin,
  Sparkles,
  Trees,
  UtensilsCrossed,
  Waves,
  Wifi,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Layout } from "@/components/layout";

export function Home() {
  const features = [
    { icon: Waves, title: "Ocean View", description: "Breathtaking views from every villa" },
    {
      icon: Sparkles,
      title: "Spa & Wellness",
      description: "Rejuvenating treatments and therapies",
    },
    { icon: UtensilsCrossed, title: "Fine Dining", description: "Exquisite cuisine and cocktails" },
    { icon: MapPin, title: "Beach Access", description: "Direct access to pristine beach" },
    { icon: Wifi, title: "Free WiFi", description: "High-speed internet throughout" },
    { icon: Car, title: "Airport Shuttle", description: "Complimentary transportation service" },
    { icon: Trees, title: "Tropical Garden", description: "Lush landscaped grounds" },
  ];

  const testimonials = [
    {
      text: "An absolutely magical experience. The beachfront villas are stunning, the service impeccable, and the attention to detail extraordinary.",
      author: "Sarah Mitchell",
      location: "London, UK",
    },
    {
      text: "The Amor exceeded all our expectations. Perfect for our honeymoon - romantic, luxurious, and unforgettable.",
      author: "James Chen",
      location: "Singapore",
    },
    {
      text: "A true paradise. The spa is world-class, the dining exceptional, and the staff made us feel like family.",
      author: "Emma Thompson",
      location: "Sydney, Australia",
    },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-[70vh] sm:h-[85vh] min-h-[600px] overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0">
          <iframe
            className="absolute top-1/2 left-1/2 w-[300%] h-[300%] -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            src="https://www.youtube.com/embed/Io0j12fYGB0?autoplay=1&mute=1&loop=1&playlist=Io0j12fYGB0&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&hd=1"
            title="The Amor Resort"
            allow="autoplay; encrypted-media"
            allowFullScreen
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        </div>

        <div className="relative h-full flex items-center justify-center text-center px-6">
          <div className="max-w-4xl">
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
              The Amor
            </h1>
            <p className="text-xl md:text-2xl text-white/95 mb-8 font-light tracking-wide">
              Timeless Elegance on Samui's Shores
            </p>
            <p className="text-lg text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed">
              Discover your private sanctuary where luxury meets tranquility. Eight exclusive
              beachfront villas await.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/rooms"
                className="bg-white text-stone-900 px-8 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-stone-100 transition-colors inline-flex items-center justify-center gap-2"
              >
                Explore Villas
                <ChevronRight size={18} />
              </Link>
              <Link
                to="/book"
                className="border-2 border-white text-white px-8 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-white hover:text-stone-900 transition-colors"
              >
                Reserve Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-stone-900 mb-6">
            Welcome to Paradise
          </h2>
          <div className="w-24 h-px bg-luxury-gold mx-auto mb-8" />
          <p className="text-lg text-stone-700 leading-relaxed mb-6">
            Nestled on the pristine shores of Samui, Thailand, The Amor offers an unparalleled
            escape into luxury and serenity. Our collection of eight exclusive beachfront villas
            combines timeless elegance with modern sophistication.
          </p>
          <p className="text-lg text-stone-700 leading-relaxed">
            Each moment at The Amor is crafted to create lasting memories - from sunrise yoga on the
            beach to sunset cocktails overlooking the Gulf of Thailand.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-4xl font-bold text-stone-900 text-center mb-4">
            Signature Experiences
          </h2>
          <p className="text-center text-stone-600 mb-12 max-w-2xl mx-auto">
            Every detail designed for your comfort and pleasure
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="bg-white p-6 border border-stone-200 text-center hover:shadow-lg transition-shadow"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-stone-900 text-white mb-4">
                    <Icon size={28} />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-stone-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-stone-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Room Preview */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-stone-900 mb-6">
                Beachfront Villas
              </h2>
              <div className="w-24 h-px bg-luxury-gold mb-8" />
              <p className="text-lg text-stone-700 leading-relaxed mb-6">
                Our eight exclusive beachfront villas are sanctuaries of peace and luxury. Each
                villa offers direct beach access, panoramic ocean views, and sophisticated interiors
                that blend Thai elegance with contemporary comfort.
              </p>
              <ul className="space-y-3 mb-8 text-stone-700">
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-luxury-gold rounded-full" />
                  King-size beds with premium linens
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-luxury-gold rounded-full" />
                  Private terrace with ocean views
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-luxury-gold rounded-full" />
                  Luxurious ensuite bathrooms
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-luxury-gold rounded-full" />
                  Direct beach access
                </li>
              </ul>
              <Link
                to="/rooms"
                className="bg-stone-800 text-white px-8 py-3.5 text-sm tracking-widest uppercase font-semibold hover:bg-stone-700 transition-colors inline-flex items-center gap-2"
              >
                View All Villas
                <ChevronRight size={18} />
              </Link>
            </div>
            <div className="relative h-[500px]">
              <img
                src="/images/villas/villa4.jpg"
                alt="Beachfront Villa"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-4xl font-bold text-center mb-4">Guest Experiences</h2>
          <p className="text-center text-stone-300 mb-12">
            Stories from those who've discovered The Amor
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-stone-800 p-8 border border-stone-700">
                <p className="text-stone-200 leading-relaxed mb-6 italic">"{testimonial.text}"</p>
                <div className="border-t border-stone-700 pt-4">
                  <p className="font-semibold text-white">{testimonial.author}</p>
                  <p className="text-sm text-stone-400">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 px-6">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/458541/pexels-photo-458541.jpeg?auto=compress&cs=tinysrgb&w=1920)",
          }}
        >
          <div className="absolute inset-0 bg-black/50" />
        </div>

        <div className="relative max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Begin Your Journey
          </h2>
          <p className="text-xl text-white/95 mb-10 leading-relaxed">
            Your sanctuary awaits. Reserve your beachfront villa and experience the timeless
            elegance of The Amor.
          </p>
          <Link
            to="/book"
            className="bg-white text-stone-900 px-10 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-stone-100 transition-colors inline-block"
          >
            Make a Reservation
          </Link>
        </div>
      </section>
    </Layout>
  );
}
